import { z } from 'zod'

import {
  ISO_DATE_REGEX,
  keepsLegacyText,
  readStoredFieldValue,
} from '@/helpers/extended-schema'

import type {
  ExtendedFieldValue,
  ExtendedFieldValues,
  ExtendedJsonSchema,
  ExtendedSchemaDerivedField,
  JsonValue,
} from '@/types/extended-schema'

export function buildZodForExtendedField(
  field: ExtendedSchemaDerivedField,
): z.ZodType<ExtendedFieldValue | undefined> {
  switch (field.type) {
    case 'string': {
      let schema = z.string()
      if (field.max_length !== undefined && field.max_length !== null) {
        schema = schema.max(field.max_length)
      }
      return field.required
        ? schema.min(1, `${field.name} is required`)
        : z.union([schema, z.literal('')]).nullish()
    }
    case 'number': {
      let schema = z
        .number()
        .refine(
          (value) => Number.isFinite(value),
          `${field.name} must be a valid number`,
        )
      if (typeof field.ge === 'number') schema = schema.min(field.ge)
      if (typeof field.le === 'number') schema = schema.max(field.le)
      return field.required ? schema : schema.nullish()
    }
    case 'boolean': {
      const schema = z.boolean()
      return field.required ? schema : schema.nullish()
    }
    case 'dropdown': {
      const options = field.options as readonly string[] | undefined
      const schema =
        options && options.length > 0
          ? z.enum(options as [string, ...string[]])
          : z.string()
      return field.required
        ? schema
        : z.union([schema, z.literal('')]).nullish()
    }
    case 'multi_select': {
      const options = field.options ?? []
      const item =
        options.length > 0
          ? z.enum(options as [string, ...string[]])
          : z.string()
      let schema = z.array(item)
      if (typeof field.max_items === 'number')
        schema = schema.max(field.max_items)
      return field.required
        ? schema.min(1, `${field.name} requires at least one selection`)
        : schema.nullish()
    }
    case 'string_list': {
      const schema = z.array(z.string())
      return field.required
        ? schema.min(1, `${field.name} requires at least one item`)
        : schema.nullish()
    }
    case 'number_list': {
      const schema = z.array(z.number())
      return field.required
        ? schema.min(1, `${field.name} requires at least one item`)
        : schema.nullish()
    }
    case 'date': {
      let schema = z
        .string()
        .regex(ISO_DATE_REGEX, `${field.name} must be an ISO-8601 date`)
      if (field.min_date) {
        schema = schema.refine(
          (value) => !value || value >= field.min_date!,
          `${field.name} must be on/after ${field.min_date}`,
        )
      }
      if (field.max_date) {
        schema = schema.refine(
          (value) => !value || value <= field.max_date!,
          `${field.name} must be on/before ${field.max_date}`,
        )
      }
      return field.required
        ? schema
        : z.union([schema, z.literal('')]).nullish()
    }
    case 'currency': {
      let schema = z.number()
      if (typeof field.ge === 'number') schema = schema.min(field.ge)
      if (typeof field.le === 'number') schema = schema.max(field.le)
      return field.required ? schema : schema.nullish()
    }
    default:
      return z.any()
  }
}

export type ExtendedValuesZodObject = z.ZodObject<
  Record<string, z.ZodType<ExtendedFieldValue | undefined>>
>

// Builds each field's schema once; a validation run then only leaves out the fields keeping their legacy text.
export function prepareExtendedValuesZodObject(
  fields: readonly ExtendedSchemaDerivedField[],
  storedValues: Readonly<Record<string, JsonValue>> = {},
): (isEdited: (fieldId: string) => boolean) => ExtendedValuesZodObject {
  const fieldSchemas = fields.map(
    (field) => [field, buildZodForExtendedField(field)] as const,
  )
  return (isEdited) =>
    z.object(
      Object.fromEntries(
        fieldSchemas
          .filter(([field]) => !keepsLegacyText(field, storedValues, isEdited))
          .map(([field, schema]) => [field.id, schema]),
      ),
    )
}

export const buildExtendedValuesZodObject = (
  schema: Pick<ExtendedJsonSchema, 'fields'> | undefined,
): ExtendedValuesZodObject | undefined =>
  schema ? prepareExtendedValuesZodObject(schema.fields)(() => true) : undefined

export function defaultValueForExtendedField(
  field: ExtendedSchemaDerivedField,
): ExtendedFieldValue {
  switch (field.type) {
    case 'string':
      return ''
    case 'number':
      return null
    case 'boolean':
      return field.default ?? false
    case 'dropdown':
      return ''
    case 'multi_select':
      return []
    case 'string_list':
      return []
    case 'number_list':
      return []
    case 'date':
      return ''
    case 'currency':
      return null
    default:
      return null
  }
}

export function buildDefaultValuesFromExtendedSchema(
  schema: Pick<ExtendedJsonSchema, 'fields'> | undefined,
  storedValues?: Readonly<Record<string, JsonValue>> | null,
): ExtendedFieldValues {
  if (!schema) {
    return {}
  }

  const values: ExtendedFieldValues = {}
  for (const field of schema.fields) {
    values[field.id] =
      readStoredFieldValue(field, storedValues?.[field.id]) ??
      defaultValueForExtendedField(field)
  }
  return values
}
